import Keycloak from "keycloak-js"
import type { KeycloakTokenParsed } from "keycloak-js"

const keycloakConfig = {
  url: process.env.NEXT_PUBLIC_KEYCLOAK_URL ?? "",
  realm: process.env.NEXT_PUBLIC_KEYCLOAK_REALM ?? "",
  clientId: process.env.NEXT_PUBLIC_KEYCLOAK_CLIENT_ID ?? "",
}

const TOKEN_REFRESH_INTERVAL_MS = 30_000
const TOKEN_MIN_VALIDITY_SECONDS = 60

let keycloak: Keycloak | null = null
let initPromise: Promise<boolean> | null = null
let refreshPromise: Promise<boolean> | null = null
let refreshIntervalId: number | null = null
const tokenChangeListeners = new Set<() => void>()

function assertBrowser() {
  if (typeof window === "undefined") {
    throw new Error("Keycloak can only be used in the browser")
  }
}

function assertConfig() {
  const missing = Object.entries(keycloakConfig)
    .filter(([, value]) => !value)
    .map(([key]) => key)

  if (missing.length > 0) {
    throw new Error(
      `Missing Keycloak config: ${missing.join(", ")}. Set NEXT_PUBLIC_KEYCLOAK_URL, NEXT_PUBLIC_KEYCLOAK_REALM, and NEXT_PUBLIC_KEYCLOAK_CLIENT_ID.`
    )
  }
}

function getKeycloak(): Keycloak {
  assertBrowser()
  assertConfig()

  if (!keycloak) {
    keycloak = new Keycloak(keycloakConfig)
  }

  return keycloak
}

function notifyTokenChanged() {
  tokenChangeListeners.forEach((listener) => listener())
}

function subscribeToTokenChanges(listener: () => void): () => void {
  tokenChangeListeners.add(listener)
  return () => {
    tokenChangeListeners.delete(listener)
  }
}

async function refreshToken(minValiditySeconds = TOKEN_MIN_VALIDITY_SECONDS): Promise<boolean> {
  const kc = getKeycloak()
  if (claimsIndicateImpersonation(tokenClaims()) && !kc.refreshToken) {
    return false
  }

  if (!refreshPromise) {
    refreshPromise = kc
      .updateToken(minValiditySeconds)
      .then((refreshed) => {
        if (refreshed) {
          // Only rewrite storage when the refreshed JWT is still an impersonation token.
          if (claimsIndicateImpersonation(tokenClaims())) {
            persistActiveImpersonationTokens({
              accessToken: kc.token ?? "",
              refreshToken: kc.refreshToken,
            })
          }
          notifyTokenChanged()
        }
        return refreshed
      })
      .catch((error) => {
        // During impersonation, do not fall back to SSO login (admin cookie session).
        // Keep the effective display name so chrome still shows the inner user.
        if (getActiveImpersonationTokens() || claimsIndicateImpersonation(tokenClaims()) || getStoredEffectiveUserName()) {
          clearActiveImpersonationTokens()
          throw error
        }
        void kc.login()
        throw error
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

function startTokenRefresh() {
  const kc = getKeycloak()

  kc.onTokenExpired = () => {
    void refreshToken(0).catch(() => {})
  }

  if (refreshIntervalId) return

  refreshIntervalId = window.setInterval(() => {
    void refreshToken().catch(() => {})
  }, TOKEN_REFRESH_INTERVAL_MS)
}

async function initKeycloak(): Promise<boolean> {
  const kc = getKeycloak()

  if (!initPromise) {
    initPromise = kc.init({
      onLoad: "login-required",
      pkceMethod: "S256",
    }).then((authenticated) => {
      if (authenticated) {
        // Token exchange does not update the browser SSO cookie. After a full
        // remount, init restores the admin session — re-apply the exchanged
        // impersonation tokens persisted in sessionStorage.
        const active = getActiveImpersonationTokens()
        if (active?.accessToken) {
          applyTokenSet({
            accessToken: active.accessToken,
            refreshToken: active.refreshToken,
          })
        }
        startTokenRefresh()
        notifyTokenChanged()
      }
      return authenticated
    })
  }

  return initPromise
}

async function requireLogin(): Promise<void> {
  const authenticated = await initKeycloak()
  if (!authenticated) {
    await getKeycloak().login()
  }
}

async function getValidToken(): Promise<string | undefined> {
  await requireLogin()
  const kc = getKeycloak()
  // Impersonation access tokens may lack a refresh token; do not call updateToken
  // with a leftover admin refresh token.
  if (claimsIndicateImpersonation(tokenClaims()) && !kc.refreshToken) {
    return kc.token
  }
  await refreshToken(30)
  return getKeycloak().token
}

function getToken(): string | undefined {
  return keycloak?.token
}

function isLoggedIn(): boolean {
  return Boolean(keycloak?.token)
}

function getTokenParsed(): KeycloakTokenParsed | undefined {
  return keycloak?.tokenParsed
}

function tokenClaims(): Record<string, unknown> | undefined {
  const parsed = keycloak?.tokenParsed
  return parsed as Record<string, unknown> | undefined
}

const IMPERSONATOR_STORAGE_KEY = "savvyco_impersonator_tokens"
/** Exchanged target-user tokens; survives remount (SSO cookie stays the admin). */
const ACTIVE_IMPERSONATION_STORAGE_KEY = "savvyco_active_impersonation_tokens"
/** Inner user display name; kept separate so token refresh failures cannot wipe chrome. */
const EFFECTIVE_USER_NAME_STORAGE_KEY = "savvyco_impersonation_effective_user_name"

export type ImpersonatorTokenBundle = {
  accessToken: string
  refreshToken?: string
  resumePath: string
  outerUserName: string
}

type ActiveImpersonationTokenBundle = {
  accessToken: string
  refreshToken?: string
}

function parseJwtPayload(token: string): KeycloakTokenParsed | undefined {
  try {
    const parts = token.split(".")
    if (parts.length < 2) return undefined
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=")
    const json = atob(padded)
    return JSON.parse(json) as KeycloakTokenParsed
  } catch {
    return undefined
  }
}

function claimsIndicateImpersonation(claims: Record<string, unknown> | undefined): boolean {
  if (!claims) return false
  if (claims.savvy_impersonation === true) return true
  if (typeof claims.savvy_impersonator_sub === "string" && claims.savvy_impersonator_sub.trim()) {
    return true
  }
  const act = claims.act
  return Boolean(
    act && typeof act === "object" && "sub" in act && typeof act.sub === "string" && act.sub.trim(),
  )
}

function isImpersonationActive(): boolean {
  if (claimsIndicateImpersonation(tokenClaims())) return true
  if (getStoredEffectiveUserName()) return true
  // After remount, claims may briefly be the admin SSO token until re-apply.
  return Boolean(getActiveImpersonationTokens())
}

function getImpersonatorDisplayName(): string | undefined {
  const claims = tokenClaims()
  if (!claims || !claimsIndicateImpersonation(claims)) return undefined
  const act = claims.act
  const fromAct =
    act && typeof act === "object" && "name" in act && typeof act.name === "string"
      ? act.name.trim()
      : ""
  const direct =
    typeof claims.savvy_impersonator_name === "string"
      ? claims.savvy_impersonator_name.trim()
      : ""
  const name = direct || fromAct
  return name || undefined
}

function getStoredEffectiveUserName(): string | undefined {
  if (typeof window === "undefined") return undefined
  const raw = window.sessionStorage.getItem(EFFECTIVE_USER_NAME_STORAGE_KEY)?.trim()
  return raw || undefined
}

function setStoredEffectiveUserName(name: string | null | undefined): void {
  assertBrowser()
  const trimmed = name?.trim()
  if (!trimmed) {
    window.sessionStorage.removeItem(EFFECTIVE_USER_NAME_STORAGE_KEY)
    return
  }
  window.sessionStorage.setItem(EFFECTIVE_USER_NAME_STORAGE_KEY, trimmed)
}

function clearStoredEffectiveUserName(): void {
  if (typeof window === "undefined") return
  window.sessionStorage.removeItem(EFFECTIVE_USER_NAME_STORAGE_KEY)
}

/** Inner (switched-to) user name for chrome; never the outer admin. */
function getEffectiveImpersonationUserName(): string | undefined {
  const stored = getStoredEffectiveUserName()
  if (stored) return stored
  if (!claimsIndicateImpersonation(tokenClaims())) return undefined
  // JWT profile claims are the target user while impersonating.
  const fromToken = displayNameFromClaims(tokenClaims())
  const impersonator = getImpersonatorDisplayName()
  // Guard: if profile name equals impersonator claim, prefer email/username instead.
  if (impersonator && fromToken === impersonator) {
    const p = tokenClaims()
    const email = typeof p?.email === "string" ? p.email.trim() : ""
    const username = typeof p?.preferred_username === "string" ? p.preferred_username.trim() : ""
    return email || username || fromToken
  }
  return fromToken !== "User" ? fromToken : undefined
}

function stashImpersonatorTokens(bundle: Omit<ImpersonatorTokenBundle, "accessToken" | "refreshToken">): void {
  assertBrowser()
  const kc = keycloak
  const accessToken = kc?.token?.trim()
  if (!accessToken) return
  const payload: ImpersonatorTokenBundle = {
    accessToken,
    refreshToken: kc?.refreshToken?.trim() || undefined,
    resumePath: bundle.resumePath,
    outerUserName: bundle.outerUserName,
  }
  window.sessionStorage.setItem(IMPERSONATOR_STORAGE_KEY, JSON.stringify(payload))
}

function getStashedImpersonatorTokens(): ImpersonatorTokenBundle | null {
  if (typeof window === "undefined") return null
  const raw = window.sessionStorage.getItem(IMPERSONATOR_STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as ImpersonatorTokenBundle
    if (!parsed?.accessToken?.trim()) return null
    return parsed
  } catch {
    return null
  }
}

function clearStashedImpersonatorTokens(): void {
  if (typeof window === "undefined") return
  window.sessionStorage.removeItem(IMPERSONATOR_STORAGE_KEY)
}

function persistActiveImpersonationTokens(tokens: {
  accessToken: string
  refreshToken?: string | null
}): void {
  assertBrowser()
  const accessToken = tokens.accessToken.trim()
  if (!accessToken) return
  const payload: ActiveImpersonationTokenBundle = {
    accessToken,
    refreshToken: tokens.refreshToken?.trim() || undefined,
  }
  window.sessionStorage.setItem(ACTIVE_IMPERSONATION_STORAGE_KEY, JSON.stringify(payload))
}

function getActiveImpersonationTokens(): ActiveImpersonationTokenBundle | null {
  if (typeof window === "undefined") return null
  const raw = window.sessionStorage.getItem(ACTIVE_IMPERSONATION_STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as ActiveImpersonationTokenBundle
    if (!parsed?.accessToken?.trim()) return null
    return parsed
  } catch {
    return null
  }
}

function clearActiveImpersonationTokens(): void {
  if (typeof window === "undefined") return
  window.sessionStorage.removeItem(ACTIVE_IMPERSONATION_STORAGE_KEY)
}

function applyTokenSet(tokens: {
  accessToken: string
  refreshToken?: string | null
}): void {
  assertBrowser()
  const kc = getKeycloak()
  const accessToken = tokens.accessToken.trim()
  if (!accessToken) {
    throw new Error("accessToken is required")
  }
  kc.token = accessToken
  // Always replace refresh token — leaving an admin refresh token after
  // impersonation causes updateToken() to silently revert to the admin session.
  const refresh = tokens.refreshToken?.trim() || ""
  kc.refreshToken = refresh || undefined
  const parsed = parseJwtPayload(accessToken)
  if (parsed) {
    kc.tokenParsed = parsed
  }
  notifyTokenChanged()
}

/** Persist + apply exchanged tokens so remount/init cannot wipe impersonation. */
function beginImpersonationSession(tokens: {
  accessToken: string
  refreshToken?: string | null
  effectiveUserName?: string | null
}): void {
  setStoredEffectiveUserName(tokens.effectiveUserName)
  persistActiveImpersonationTokens(tokens)
  applyTokenSet(tokens)
}

function restoreImpersonatorTokens(): ImpersonatorTokenBundle | null {
  const bundle = getStashedImpersonatorTokens()
  clearActiveImpersonationTokens()
  clearStoredEffectiveUserName()
  if (!bundle) return null
  applyTokenSet({
    accessToken: bundle.accessToken,
    refreshToken: bundle.refreshToken,
  })
  clearStashedImpersonatorTokens()
  return bundle
}

/** Prefer OIDC `name`; then given+family; then username; then email. */
function displayNameFromClaims(p: Record<string, unknown> | undefined): string {
  if (!p) return "User"
  if (typeof p.name === "string" && p.name.trim()) return p.name.trim()
  const given = typeof p.given_name === "string" ? p.given_name.trim() : ""
  const family = typeof p.family_name === "string" ? p.family_name.trim() : ""
  const combined = `${given} ${family}`.trim()
  if (combined) return combined
  if (typeof p.preferred_username === "string" && p.preferred_username.trim()) {
    return p.preferred_username.trim()
  }
  if (typeof p.email === "string" && p.email.trim()) return p.email.trim()
  return "User"
}

function getDisplayName(): string {
  // While impersonating, never surface the outer admin name in chrome.
  if (isImpersonationActive()) {
    const effective = getEffectiveImpersonationUserName()
    if (effective) return effective
  }
  return displayNameFromClaims(tokenClaims())
}

function getEmail(): string | undefined {
  const e = tokenClaims()?.email
  return typeof e === "string" && e.trim() ? e.trim() : undefined
}

/** Two-letter initials from a display name (e.g. JWT `name`). */
function initialsFromDisplayName(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return "?"
  const parts = trimmed.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    const a = parts[0][0]
    const b = parts[parts.length - 1][0]
    return `${a}${b}`.toUpperCase()
  }
  if (parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase()
  return parts[0][0]!.toUpperCase()
}

function getUserInitials(): string {
  return initialsFromDisplayName(getDisplayName())
}

function getUsername(): string | undefined {
  return keycloak?.tokenParsed?.preferred_username
}

function hasRealmRole(roles: string[]): boolean {
  return roles.some((role) => Boolean(keycloak?.hasRealmRole(role)))
}

function login(): Promise<void> {
  return getKeycloak().login()
}

function logout(): Promise<void> {
  return getKeycloak().logout({ redirectUri: window.location.origin })
}

export const UserService = {
  initKeycloak,
  requireLogin,
  login,
  logout,
  isLoggedIn,
  getToken,
  getValidToken,
  subscribeToTokenChanges,
  getTokenParsed,
  getDisplayName,
  getEmail,
  getUserInitials,
  initialsFromDisplayName,
  getUsername,
  hasRealmRole,
  isImpersonationActive,
  getImpersonatorDisplayName,
  getEffectiveImpersonationUserName,
  stashImpersonatorTokens,
  getStashedImpersonatorTokens,
  clearStashedImpersonatorTokens,
  clearActiveImpersonationTokens,
  clearStoredEffectiveUserName,
  applyTokenSet,
  beginImpersonationSession,
  restoreImpersonatorTokens,
}
